import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Youtube, Eye, Heart, MessageCircle, RefreshCw, Loader2, AlertTriangle, Gauge } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export const fmt = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : `${n}`);

export const PubStats = ({ clip, onClip }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const pubs = clip.publications || [];
  const uploading = pubs.some((p) => p.status === "uploading");
  const refresh = async (quiet) => {
    setBusy(!quiet);
    try { const { data } = await api.post(`/clips/${clip.id}/stats/refresh`); onClip(data); }
    catch (e) { if (!quiet) toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => {
    if (!uploading) return undefined;
    const id = setInterval(() => refresh(true), 5000);
    return () => clearInterval(id);
  }, [uploading]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pubs.length) return null;
  return (
    <div className="mt-3 space-y-1.5" data-testid="pub-stats">
      {pubs.map((p) => (
        <div key={p.id} className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-2 text-[11px]" data-testid={`pub-row-${p.id}`}>
          <div className="flex items-center gap-1.5">
            <Youtube className="w-3.5 h-3.5 text-[#ff0033] shrink-0" />
            {p.status === "published" ? <a href={p.url} target="_blank" rel="noreferrer" className="text-white hover:underline truncate flex-1" data-testid="pub-link">{t("pub.on_youtube")}</a>
              : p.status === "uploading" ? <span className="text-slate-400 flex items-center gap-1 flex-1" data-testid="pub-uploading"><Loader2 className="w-3 h-3 animate-spin" />{t("pub.uploading")}</span>
              : <span className="text-rose-400 flex items-center gap-1 flex-1 truncate" title={p.error} data-testid="pub-error"><AlertTriangle className="w-3 h-3" />{p.error || t("pub.failed")}</span>}
            {p.status === "published" && <button onClick={() => refresh(false)} disabled={busy} title={t("pub.refresh")} className="text-slate-400 hover:text-white" data-testid="pub-refresh-btn">
              <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /></button>}
          </div>
          {p.status === "published" && (
            <div className="flex items-center gap-3 mt-1.5 font-mono text-white">
              <span className="flex items-center gap-1" data-testid="pub-views"><Eye className="w-3 h-3 text-[#3ad7ff]" />{fmt(p.stats?.views ?? 0)}</span>
              <span className="flex items-center gap-1" data-testid="pub-likes"><Heart className="w-3 h-3 text-[#ff5a8a]" />{fmt(p.stats?.likes ?? 0)}</span>
              <span className="flex items-center gap-1" data-testid="pub-comments"><MessageCircle className="w-3 h-3 text-[#ffcc4d]" />{fmt(p.stats?.comments ?? 0)}</span>
              {clip.viral?.score != null && <span className="ml-auto flex items-center gap-1 text-[#c79bff]" title={t("pub.vs_score")} data-testid="pub-viral-score"><Gauge className="w-3 h-3" />{clip.viral.score}/100</span>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
